import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useSelectableContext } from 'hooks/useSelectable';
import Submission from 'types/Submission';
import SubmissionThumbnail from './SubmissionThumbnail/SubmissionThumbnail';
import Loader from 'components/Loader';
import RenderIfVisible from 'components/RenderIfVisible';
import SubmissionThumbnailContainer from './SubmissionThumbnailContainer';
import UgcRequest from 'types/UgcRequest';
import { useEffect, useMemo, useRef, useState } from 'react';
import { buildMortarRows } from './buildMortarRows';
import { useDebouncedCallback } from 'use-debounce';
import clsx from 'clsx';

interface Props {
  submissions: Submission[];
  ugcRequest: UgcRequest;
  isFetching: boolean;
  loadNextPage: () => void;
  hasNextPage: boolean;
}

const THUMB_SIZE = 260;

export default function SubmissionList(props: Props) {
  const { submissions, ugcRequest, isFetching, loadNextPage, hasNextPage } =
    props;
  const { selectedIds, toggleSelected } = useSelectableContext();
  const [sentryRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage,
    onLoadMore: hasNextPage ? loadNextPage : () => {},
    disabled: !submissions.length,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const debouncedSetContainerWidth = useDebouncedCallback((width: number) => {
    setContainerWidth(width);
  }, 151);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      debouncedSetContainerWidth(width);
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      debouncedSetContainerWidth.cancel();
    };
  }, [debouncedSetContainerWidth]);

  const rows = useMemo(() => {
    return containerWidth
      ? buildMortarRows({
          items: submissions,
          containerWidth,
          thumbSize: THUMB_SIZE,
          gap: 8,
          minAspectRatio: 0.75,
        })
      : [];
  }, [submissions, containerWidth]);

  return (
    <>
      <div className="submission-list" ref={containerRef}>
        {rows.map((row, rowIndex) => {
          const isFinalRow = rowIndex === rows.length - 1;
          return (
            <div
              key={rowIndex}
              className="mb-0.75 flex gap-0.75"
              style={{ height: `${row.height}px` }}
            >
              {row.rowItems.map(({ item, ratio }) => {
                const width = row.height * ratio;
                return (
                  <div
                    key={item.id}
                    className={clsx(isFinalRow ? undefined : 'flex-1')}
                    style={
                      isFinalRow
                        ? { width: `${width}px`, height: '100%' }
                        : { flex: ratio }
                    }
                  >
                    <RenderIfVisible
                      renderPlaceholder={() => <SubmissionThumbnailContainer />}
                      wrapperProps={{ className: 'w-full h-full' }}
                    >
                      <SubmissionThumbnail
                        ugcRequest={ugcRequest}
                        submission={item}
                        isSelectMode={!!selectedIds.length}
                        isSelected={selectedIds.includes(item.id)}
                        onToggleSelected={() => toggleSelected(item.id)}
                      />
                    </RenderIfVisible>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {(hasNextPage || isFetching) && (
        <div ref={sentryRef}>
          <Loader />
        </div>
      )}
    </>
  );
}
